import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { MODULE_CATALOG } from '../../config/moduleCatalog';

/**
 * Platform Capabilities — a surface describing what the SaaS offers and the
 * tenancy/role model it enforces. Reference-style page for the operator.
 *
 * The "Event modules" table is driven by the catalog (GET /platform/capabilities),
 * enriched with cross-tenant adoption counts. Read-only: the catalog lives in
 * code because each module is wired to real features, not just metadata.
 */

interface Capability {
  icon: string;
  title: string;
  blurb: string;
  status: 'Live' | 'Planned';
}

interface ModuleCapability {
  key: string;
  label: string;
  description: string;
  icon: string;
  isBase: boolean;
  dependsOn: string[];
  tenantsEnabled: number;
}
interface CapabilitiesResponse {
  modules: ModuleCapability[];
  totalTenants: number;
}

const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  MODULE_CATALOG.map((m) => [m.key, m.label]),
);

const PLATFORM: Capability[] = [
  { icon: '🏢', title: 'Multi-tenant isolation', blurb: 'Row-level tenant scoping via Prisma middleware — one tenant can never see another’s data.', status: 'Live' },
  { icon: '🌐', title: 'Custom domains & subdomains', blurb: 'Each tenant on its own hub subdomain, or bring its own verified domain.', status: 'Live' },
  { icon: '🔐', title: 'Role tiers', blurb: 'Platform Admin/User over Customer Super User / Editor / User, enforced per request.', status: 'Live' },
  { icon: '🧑\u200d🤝\u200d🧑', title: 'Tenant user management', blurb: 'Super Users invite and manage their own team within a tenant.', status: 'Live' },
  { icon: '⏸️', title: 'Suspend / reactivate', blurb: 'Operators can suspend a tenant — its hub stops resolving instantly.', status: 'Live' },
  { icon: '💳', title: 'Billing & self-serve signup', blurb: 'Metered billing and self-service onboarding.', status: 'Planned' },
  { icon: '🎨', title: 'Per-tenant branding', blurb: 'Tenant logos, colors, and theming on their hub.', status: 'Planned' },
];

const ROLES: { role: string; scope: string; can: string }[] = [
  { role: 'Platform Admin', scope: 'All tenants', can: 'Full control — create/suspend tenants, manage any tenant’s users, configure the platform.' },
  { role: 'Platform User', scope: 'All tenants', can: 'Operator support — build events for a customer; cannot delete tenants or change platform config.' },
  { role: 'Customer Super User', scope: 'One tenant', can: 'Full control within their tenant, including managing that tenant’s users.' },
  { role: 'Customer Editor', scope: 'One tenant', can: 'Create/manage events, guests, seating, tickets, check-in — no user management.' },
  { role: 'Customer User', scope: 'One tenant', can: 'Read-only — view guests, seating, and reports.' },
  { role: 'Guest', scope: 'Self', can: 'Token/QR access to their own card — not a login role.' },
];

export function PlatformCapabilitiesPage() {
  const [caps, setCaps] = useState<CapabilitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient<CapabilitiesResponse>('/platform/capabilities');
        if (!cancelled) setCaps(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load capabilities');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Capabilities</h1>
          <p style={styles.sub}>What the platform offers tenants, and the model it enforces.</p>
        </div>
      </div>

      <h2 style={styles.sectionTitle}>
        Event modules
        {caps && <span style={styles.sectionCount}> · {caps.totalTenants} organization{caps.totalTenants === 1 ? '' : 's'}</span>}
      </h2>
      <section style={styles.card}>
        {error && <div style={styles.error}>{error}</div>}
        {loading ? (
          <div style={styles.muted}>Loading modules…</div>
        ) : (
          <table style={styles.table} className="ep-cap-table">
            <thead>
              <tr>
                <th style={styles.th}>Module</th>
                <th style={styles.th}>Tier</th>
                <th style={styles.th}>Depends on</th>
                <th style={styles.thNum}>Orgs using</th>
              </tr>
            </thead>
            <tbody>
              {(caps?.modules ?? []).map((m) => (
                <tr key={m.key}>
                  <td style={styles.td} data-label="Module">
                    <div style={styles.modCell}>
                      <span style={styles.modIcon}>{m.icon}</span>
                      <span>
                        <span style={styles.modLabel}>{m.label}</span>
                        <span style={styles.modDesc}>{m.description}</span>
                      </span>
                    </div>
                  </td>
                  <td style={styles.td} data-label="Tier">
                    <span style={m.isBase ? styles.baseBadge : styles.optionalBadge}>
                      {m.isBase ? 'Base' : 'Optional'}
                    </span>
                  </td>
                  <td style={styles.td} data-label="Depends on">
                    {m.dependsOn.length === 0 ? (
                      <span style={styles.dash}>—</span>
                    ) : (
                      m.dependsOn.map((d) => (
                        <span key={d} style={styles.depChip}>
                          {LABEL_BY_KEY[d] ?? d}
                        </span>
                      ))
                    )}
                  </td>
                  <td style={styles.tdNum} data-label="Orgs using">
                    {m.tenantsEnabled}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <h2 style={styles.sectionTitle}>Platform capabilities</h2>
      <div style={styles.cardGrid}>
        {PLATFORM.map((c) => (
          <CapabilityCard key={c.title} c={c} />
        ))}
      </div>

      <h2 style={styles.sectionTitle}>Role model</h2>
      <section style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Scope</th>
              <th style={styles.th}>Capabilities</th>
            </tr>
          </thead>
          <tbody>
            {ROLES.map((r) => (
              <tr key={r.role}>
                <td style={{ ...styles.td, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.role}</td>
                <td style={styles.td}>
                  <span style={styles.scopePill}>{r.scope}</span>
                </td>
                <td style={{ ...styles.td, color: '#334155' }}>{r.can}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Mobile: collapse the modules + roles tables into stacked cards. */}
      <style>{`
        @media (max-width: 680px) {
          .ep-cap-table thead { display: none; }
          .ep-cap-table, .ep-cap-table tbody, .ep-cap-table tr, .ep-cap-table td {
            display: block;
            width: 100%;
          }
          .ep-cap-table tr {
            border-bottom: 8px solid #f1f5f9;
            padding: 6px 0;
          }
          .ep-cap-table td {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            text-align: right;
            padding: 6px 10px;
            border: none;
          }
          .ep-cap-table td::before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #94a3b8;
            text-align: left;
            flex-shrink: 0;
          }
        }
      `}</style>
    </div>
  );
}

function CapabilityCard({ c }: { c: Capability }) {
  return (
    <div style={styles.capCard}>
      <div style={styles.capTop}>
        <span style={styles.capIcon}>{c.icon}</span>
        <span style={c.status === 'Live' ? styles.liveBadge : styles.plannedBadge}>{c.status}</span>
      </div>
      <div style={styles.capTitle}>{c.title}</div>
      <div style={styles.capBlurb}>{c.blurb}</div>
    </div>
  );
}

const ACCENT = '#4f46e5';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { marginBottom: 8 },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0' },
  sectionTitle: { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, margin: '24px 0 12px' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 },
  capCard: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  capTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  capIcon: { fontSize: '1.5rem' },
  liveBadge: { background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700 },
  plannedBadge: { background: '#f1f5f9', color: '#64748b', borderRadius: 999, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700 },
  capTitle: { fontWeight: 700, color: INK, fontSize: '0.95rem' },
  capBlurb: { color: MUTED, fontSize: '0.82rem', marginTop: 4, lineHeight: 1.45 },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  thNum: { textAlign: 'right', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  tdNum: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: INK },
  scopePill: { background: '#eef2ff', color: ACCENT, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' },
  sectionCount: { color: MUTED, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  muted: { color: '#94a3b8', fontSize: '0.9rem', padding: '0.5rem' },
  error: { color: '#b91c1c', background: '#fef2f2', padding: '0.6rem 0.9rem', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem' },
  modCell: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  modIcon: { fontSize: '1.2rem', lineHeight: 1.2 },
  modLabel: { display: 'block', fontWeight: 700, color: INK, fontSize: '0.9rem' },
  modDesc: { display: 'block', color: MUTED, fontSize: '0.78rem', marginTop: 2, lineHeight: 1.4 },
  baseBadge: { background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700 },
  optionalBadge: { background: '#eef2ff', color: ACCENT, borderRadius: 999, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700 },
  dash: { color: '#cbd5e1' },
  depChip: { display: 'inline-block', background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 600, marginRight: 4 },
};
